import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { uiJiraLossAversionHasPaymentQuery } from '@atlassian/jira-relay/src/__generated__/uiJiraLossAversionHasPaymentQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	useJiraLossAversionAAExperiment,
	useJiraLossAversionABExperiment,
} from '../services/use-experiment/use-experiment';
import { JiraLossAversionModal } from './loss-aversion-modal';

// Preferred HOC but we need to get sign off from a FE architect to introduce new HOCs to CFE
export const JiraLossAversionExperimentInternals = () => {
	const cloudId = useCloudId();
	const shouldFetch = fg('jira_loss_aversion_standard_gql_has_billing');
	const data = useLazyLoadQuery<uiJiraLossAversionHasPaymentQuery>(
		graphql`
			query uiJiraLossAversionHasPaymentQuery(
				$cloudId: ID!
				$productKey: String!
				$shouldFetch: Boolean!
			) {
				tenantContexts(cloudIds: [$cloudId]) @include(if: $shouldFetch) {
					entitlementInfo(hamsProductKey: $productKey) {
						entitlement(where: { AND: [{ inTrialOrPreDunning: true }] }) {
							subscription {
								accountDetails {
									invoiceGroup {
										invoiceable @required(action: THROW)
									}
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId, productKey: 'jira-software.ondemand', shouldFetch },
	);

	// if we did not fetch the data because the jira_loss_aversion_standard_gql_has_billing flag is off
	// we consider the user as not having billing, making them eligible for the experiment
	const hasBilling = Boolean(
		data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails
			?.invoiceGroup?.invoiceable,
	);

	// AA experiment
	const aaExperimentIsEnabled = fg('jira_loss_aversion_standard_gate_aa');
	// This function should fire all necessary exposure events for AA experiment
	useJiraLossAversionAAExperiment({
		isExperimentEnabled: aaExperimentIsEnabled,
		extraAnalytics: {},
		hasBilling,
	});

	// AB experiment
	const abExperimentIsEnabled = fg('jira_loss_aversion_standard_gate_ab');
	// TODO: use cohort value?
	const { renderTreatment } = useJiraLossAversionABExperiment({
		isExperimentEnabled: abExperimentIsEnabled,
		extraAnalytics: {},
		hasBilling,
	});

	if (renderTreatment) {
		return <JiraLossAversionModal />;
	}
	return <></>;
};

export const JiraLossAversionExperiment = () => (
	<JSErrorBoundary
		id="jiraLossAversionExperiment"
		packageName="jiraLossAversion"
		teamName="luna-growth"
		fallback="unmount"
	>
		<JiraLossAversionExperimentInternals />
	</JSErrorBoundary>
);
